import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, ExternalLink, Loader2, Users, AlertCircle, Mail, ArrowUpDown, ArrowUp, ArrowDown, Filter, ChevronDown, X, Check } from 'lucide-react';
import { CanEdit, ReadOnlyBadge } from '@components/common/RequirePermission';

// Common titles to strip when generating initials from names
const TITLES = ['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'rev', 'sir', 'dame', 'lord', 'lady'];

function getInitials(name: string): string {
  const parts = name.split(' ').filter(p => !TITLES.includes(p.toLowerCase()));
  if (parts.length === 0) return name.charAt(0).toUpperCase();
  // Use first and last name parts
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

interface Person {
  id: number;
  name: string;
  email: string | null;
  type: string | null;
  lastModified: string | null;
}

interface SearchResponse {
  data: Person[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
  beaconAccountId?: string;
  message?: string;
}

type SortField = 'name' | 'email' | 'updated_at';
type SortDirection = 'asc' | 'desc';

const MIN_SEARCH_LENGTH = 3;
const PAGE_SIZE = 20;

export default function People() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [results, setResults] = useState<Person[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [beaconAccountId, setBeaconAccountId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>('updated_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  // Type filter state
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Fetch available types on mount
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
        const response = await fetch(`${apiBase}/api/beacon/people/types`);
        if (response.ok) {
          const data = await response.json();
          setAvailableTypes(data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch person types:', err);
      }
    };
    fetchTypes();
  }, []);

  // Close type dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setShowTypeDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset results when search or filters change
  useEffect(() => {
    setResults([]);
    setPage(1);
    setHasMore(false);
  }, [debouncedSearch, sortBy, sortDir, selectedTypes]);

  // Perform search when debounced value changes
  const performSearch = useCallback(async (query: string, pageNum: number, append: boolean = false, types: string[] = []) => {
    if (query.length < MIN_SEARCH_LENGTH && types.length === 0) {
      setResults([]);
      setTotal(0);
      setHasSearched(false);
      setHasMore(false);
      return;
    }

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    setHasSearched(true);

    try {
      const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
      const params = new URLSearchParams({
        limit: PAGE_SIZE.toString(),
        page: pageNum.toString(),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (query.length >= MIN_SEARCH_LENGTH) {
        params.set('search', query);
      }
      if (types.length > 0) {
        params.set('types', types.join(','));
      }
      const response = await fetch(`${apiBase}/api/beacon/people?${params}`);

      if (!response.ok) {
        throw new Error('Failed to search people');
      }

      const data: SearchResponse = await response.json();

      if (append) {
        setResults(prev => [...prev, ...data.data]);
      } else {
        setResults(data.data);
      }

      setTotal(data.pagination.total);
      setHasMore(data.pagination.hasMore);

      if (data.beaconAccountId) {
        setBeaconAccountId(data.beaconAccountId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      if (!append) {
        setResults([]);
        setTotal(0);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [sortBy, sortDir]);

  // Initial search
  useEffect(() => {
    performSearch(debouncedSearch, 1, false, selectedTypes);
  }, [debouncedSearch, sortBy, sortDir, selectedTypes, performSearch]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          const nextPage = page + 1;
          setPage(nextPage);
          performSearch(debouncedSearch, nextPage, true, selectedTypes);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, isLoading, page, debouncedSearch, performSearch]);

  // Open person in Beacon CRM with correct URL format
  const openInBeacon = (personId: number) => {
    if (beaconAccountId) {
      window.open(`https://app.beaconcrm.org/account/${beaconAccountId}/record/person/${personId}`, '_blank');
    }
  };

  // Handle column sort
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  // Get sort icon for column
  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDir === 'asc'
      ? <ArrowUp className="h-4 w-4 text-sca-purple-600" />
      : <ArrowDown className="h-4 w-4 text-sca-purple-600" />;
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Type filter helpers
  const toggleType = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const clearTypes = () => {
    setSelectedTypes([]);
  };

  const showMinCharsHint = searchTerm.length > 0 && searchTerm.length < MIN_SEARCH_LENGTH && selectedTypes.length === 0;

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-1 gap-3 items-start">
          {/* Search input */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                placeholder="Search people by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
                autoFocus
              />
              {isLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
              )}
            </div>
            {showMinCharsHint && (
              <p className="text-xs text-gray-500 mt-1">
                Type at least {MIN_SEARCH_LENGTH} characters to search
              </p>
            )}
          </div>

          {/* Type filter dropdown */}
          {availableTypes.length > 0 && (
            <div className="relative" ref={typeDropdownRef}>
              <button
                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors ${
                  selectedTypes.length > 0 ? 'border-sca-purple-500 bg-sca-purple-50' : 'border-gray-300'
                }`}
              >
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm">
                  {selectedTypes.length > 0 ? `Type (${selectedTypes.length})` : 'Type'}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>

              {showTypeDropdown && (
                <div className="absolute z-10 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="p-2 border-b border-gray-100 flex justify-between items-center">
                    <span className="text-xs font-medium text-gray-500 uppercase">Filter by Type</span>
                    {selectedTypes.length > 0 && (
                      <button
                        onClick={clearTypes}
                        className="text-xs text-sca-purple-600 hover:text-sca-purple-700"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="max-h-60 overflow-y-auto p-2">
                    {availableTypes.map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                        onClick={() => toggleType(type)}
                      >
                        <div
                          className={`w-4 h-4 border rounded flex items-center justify-center ${
                            selectedTypes.includes(type)
                              ? 'bg-sca-purple-600 border-sca-purple-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {selectedTypes.includes(type) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 items-center">
          <ReadOnlyBadge />
          <CanEdit>
            <a
              href="https://app.beaconcrm.org/account/29871/records/person/list"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add in Beacon
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </CanEdit>
        </div>
      </div>

      {/* Active filter chips */}
      {selectedTypes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTypes.map((type) => (
            <span
              key={type}
              className="inline-flex items-center gap-1 px-2 py-1 bg-sca-purple-100 text-sca-purple-700 text-xs font-medium rounded-full"
            >
              {type}
              <button
                onClick={() => toggleType(type)}
                className="hover:bg-sca-purple-200 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Search failed</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Results count */}
      {hasSearched && !isLoading && !error && (
        <p className="text-sm text-gray-500">
          {total === 0
            ? `No people found matching "${debouncedSearch}"`
            : `Found ${total} ${total === 1 ? 'person' : 'people'} matching "${debouncedSearch}"`}
        </p>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center gap-2">
                      Email
                      {getSortIcon('email')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="flex items-center gap-2">
                      Last Modified
                      {getSortIcon('updated_at')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {results.map((person) => (
                  <tr
                    key={person.id}
                    onClick={() => openInBeacon(person.id)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 rounded-full bg-sca-purple-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-sca-purple-600">
                            {getInitials(person.name)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{person.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {person.email ? (
                        <a
                          href={`mailto:${person.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-sca-purple-600 hover:text-sca-purple-700 hover:underline group relative"
                          title={person.email}
                        >
                          <Mail className="h-4 w-4 flex-shrink-0" />
                          <span className="hidden md:inline">{person.email}</span>
                          {/* Mobile tooltip on hover/focus */}
                          <span className="md:hidden absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 group-focus:opacity-100 pointer-events-none whitespace-nowrap z-10 transition-opacity">
                            {person.email}
                          </span>
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {person.type ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {person.type}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(person.lastModified)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <ExternalLink className="h-4 w-4 text-gray-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load more indicator */}
          <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
            {isLoadingMore && (
              <Loader2 className="h-5 w-5 text-sca-purple-500 animate-spin" />
            )}
          </div>
        </div>
      )}

      {/* Empty state - no search yet */}
      {!hasSearched && !isLoading && (
        <div className="text-center py-16 card">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Search for People</h3>
          <p className="text-gray-500 max-w-sm mx-auto">
            Enter at least {MIN_SEARCH_LENGTH} characters to search for people in Beacon CRM.
            Results will appear here.
          </p>
        </div>
      )}

      {/* Empty state - no results */}
      {hasSearched && !isLoading && results.length === 0 && !error && (
        <div className="text-center py-16 card">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No People Found</h3>
          <p className="text-gray-500 max-w-sm mx-auto">
            No people match your search "{debouncedSearch}". Try a different search term or add a new person in Beacon.
          </p>
          <CanEdit>
            <a
              href="https://app.beaconcrm.org/account/29871/records/person/list"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-sca-purple-600 hover:text-sca-purple-700"
            >
              <Plus className="h-4 w-4" />
              Add person in Beacon
              <ExternalLink className="h-3 w-3" />
            </a>
          </CanEdit>
        </div>
      )}

      {/* Integration note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> Search results are fetched directly from Beacon CRM.
          Click on any row to view the full record in{' '}
          <a
            href="https://app.beaconcrm.org"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline"
          >
            Beacon CRM
          </a>
          .
        </p>
      </div>
    </div>
  );
}
